import React, { Component, useMemo, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { fetchLinkedSources, fetchSupportedSources } from '../redux/actions';

import Loading from './Loading';
import LoadingDial from '../components/loading/LoadingDial';
import SupportedSourceCard from '../components/availableSources/SupportedSourceCard';
import LinkedSourceCard from '../components/availableSources/LinkedSourceCard';
import { useCallback } from 'react';
import { Search } from '@mui/icons-material';
import ActiveGrayed from '../assets/main-logo-active.svg';

import { getLocalization, getMultipleSources } from '../redux/selectors';

const FlowSources = (props) => {
  const { selectedFlow, flows, linked, supported, fetchLinkedSources, fetchSupportedSources } = props;
  const [term, setTerm] = useState('');
  const localization = getLocalization();
  const multipleSources = getMultipleSources();
  // If multiple sources are enabled, we need the hasLinkedSource flag to be false always
  const hasLinkedSource = multipleSources ? false : (linked && Object.entries(linked).length > 0);

  useEffect(() => {
    if (!linked) {
      fetchLinkedSources();
    }
  }, [linked]);

  const byLabel = useCallback(([k1, v1], [k2, v2]) => {
    if (v1.label < v2.label) return -1;
    if (v1.label > v2.label) return 1;
    return 0;
  }, []);

  let linkedSourceComponents = useMemo(
    () =>
      linked &&
      Object.entries(linked)
        .sort(byLabel)
        .filter(([key, value]) => !term || term.length < 1 || value.label.toLowerCase().includes(term.toLowerCase()))
        .map(([key, value]) => <LinkedSourceCard key={key} source={value} />),
    [linked, term]
  );

  let supportedSourceComponents = useMemo(
    () =>
      supported &&
      linked &&
      Object.entries(supported)
        .filter(([key, value]) => linked[key] == undefined)
        .sort(byLabel)
        .filter(([key, value]) => !term || term.length < 1 || value.label.toLowerCase().includes(term.toLowerCase()))
        .map(([key, value]) => <SupportedSourceCard key={key} source={value} hasLinkedSource={hasLinkedSource} />),
    [supported, linked, term]
  );

  const loading = !linked || !supported;
  const noSources =
    linkedSourceComponents &&
    linkedSourceComponents.length == 0 &&
    supportedSourceComponents &&
    supportedSourceComponents.length == 0;
  const noSourcesLabel = term
    ? ( localization?.searchSource?.noSourceCaption || 'No sources found with this search term.')
    : ( localization?.searchSource?.noSourceAvailableCaption || 'No sources found. Add your first source in the admin panel!')

  const icon = selectedFlow?.flowIcon || ActiveGrayed;
  return (
    <Grid container>
      <Box position="sticky" top={0} bgcolor="background.paper" width="100%" zIndex={10000}>
        <Grid item xs={12}>
          <Box display="flex" gap={1.5}>
            <Box
              component="img"
              role='img'
              alt={`flow icon`}
              src={icon}
              className="img"
              width={32}
              height={32}
            />
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="h1">{selectedFlow?.name}</Typography>
              <Typography variant="body1">{selectedFlow?.description}</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} mt={2} pb={1} display={loading || flows.processingOAuth ? 'none' : 'flex'}>
          <TextField
            name="search"
            label={ localization?.searchSource?.searchSourceText || 'Search Source'}
            variant="standard"
            fullWidth
            onChange={(e) => setTerm(e.target.value)}
            InputProps={{
              endAdornment: <Search sx={{ color: '#ccc', fontSize: '16px' }}></Search>,
            }}
          />
        </Grid>
      </Box>
      {(loading || flows.processingOAuth) && (
        <Grid
          item
          xs={12}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(600px - 200px)', flexDirection:'column' }}
        >
          <LoadingDial />
        </Grid>
      )}
      <Box
        display="grid"
        sx={{
          width: '100%',
          pt: 1,
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
          },
          '& > div:last-child': {
            mb: 2,
          },
        }}
      >
        {!loading && !flows.processingOAuth && [linkedSourceComponents, supportedSourceComponents]}
      </Box>
      {noSources && <Typography color="text.main">{noSourcesLabel}</Typography>}
    </Grid>
  );
};

const mapStateToProps = (state, props) => {
  const flowId = state.flows.selectedFlow;

  return {
    selectedFlow: state.flows.supportedFlows[flowId],
    flows: state.flows,
    supported: state.sources.supported[flowId],
    linked: state.sources.linked,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchLinkedSources: () => dispatch(fetchLinkedSources()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FlowSources);
